import OpenSeadragon from 'openseadragon';

export const COREG_META_KEY = 'coreg';
export const COREG_VERSION = 1;

export const NUDGE_STEPS = {
  x: 0.003,
  y: 0.003,
  scale: 0.01,
  angle: 0.5,
};

export function nudgeMovingLayer(movingTiledImage, axis, direction) {
  if (!movingTiledImage || direction === 0) return;

  const sign = direction > 0 ? 1 : -1;
  const bounds = movingTiledImage.getBoundsNoRotate(false);

  switch (axis) {
    case 'x':
      movingTiledImage.setPosition(
        new OpenSeadragon.Point(bounds.x + sign * NUDGE_STEPS.x, bounds.y),
        false
      );
      break;
    case 'y':
      movingTiledImage.setPosition(
        new OpenSeadragon.Point(bounds.x, bounds.y + sign * NUDGE_STEPS.y),
        false
      );
      break;
    case 'scale': {
      const factor = 1 + sign * NUDGE_STEPS.scale;
      movingTiledImage.setWidth(Math.max(bounds.width * factor, 1e-6), false);
      break;
    }
    case 'angle':
      movingTiledImage.setRotation(
        movingTiledImage.getRotation(false) + sign * NUDGE_STEPS.angle,
        false
      );
      break;
    default:
      break;
  }
}

export function opacityToVisibilityLabel(opacity) {
  if (opacity >= 1) return '100%';
  if (opacity >= 0.25) return '50%';
  return '0%';
}

export function captureTiledImageState(tiledImage) {
  const bounds = tiledImage.getBoundsNoRotate(false);
  return {
    position: { x: bounds.x, y: bounds.y },
    width: bounds.width,
    rotation: tiledImage.getRotation(false),
    flip: tiledImage.getFlip(),
    opacity: tiledImage.opacity,
  };
}

export function applyTiledImageTransform(tiledImage, state) {
  if (!tiledImage || !state) return;
  tiledImage.setPosition(new OpenSeadragon.Point(state.position.x, state.position.y), false);
  tiledImage.setWidth(state.width, false);
  tiledImage.setRotation(state.rotation, false);
  tiledImage.setFlip(state.flip);
}

export function applyTiledImageState(tiledImage, state) {
  if (!tiledImage || !state) return;
  applyTiledImageTransform(tiledImage, state);
  tiledImage.setOpacity(state.opacity);
}

/** Stack moving layer on static with no registration transform applied yet. */
export function layoutUnregisteredOverlay(staticTiledImage, movingTiledImage) {
  if (!staticTiledImage || !movingTiledImage) return;
  const staticBounds = staticTiledImage.getBoundsNoRotate(false);
  movingTiledImage.setRotation(0, false);
  movingTiledImage.setFlip(false);
  movingTiledImage.setWidth(staticBounds.width, false);
  movingTiledImage.setPosition(
    new OpenSeadragon.Point(staticBounds.x, staticBounds.y),
    false
  );
}

/**
 * Apply a similarity transform to align the moving layer onto the static layer
 * using two point pairs (ported from pitt-bdsa/webapps coreg/app.mjs).
 */
export function applyTwoPointRegistration(movingTiledImage, staticTiledImage, points) {
  const { moveA, moveB, ontoA, ontoB } = points;

  const posA1 = movingTiledImage.imageToViewportCoordinates(moveA.x, moveA.y, false);
  const posA2 = movingTiledImage.imageToViewportCoordinates(moveB.x, moveB.y, false);
  const posB1 = staticTiledImage.imageToViewportCoordinates(ontoA.x, ontoA.y, false);
  const posB2 = staticTiledImage.imageToViewportCoordinates(ontoB.x, ontoB.y, false);

  const distA = posA2.distanceTo(posA1);
  const distB = posB2.distanceTo(posB1);
  if (distA === 0) {
    throw new Error('The two points on the moving image must be different.');
  }

  const scaleFactor = distB / distA;

  const angleA = (Math.atan2(posA2.y - posA1.y, posA2.x - posA1.x) * 180) / Math.PI;
  const angleB = (Math.atan2(posB2.y - posB1.y, posB2.x - posB1.x) * 180) / Math.PI;
  const deltaAngle = angleB - angleA;

  movingTiledImage.setWidth(movingTiledImage.getBoundsNoRotate(false).width * scaleFactor, false);
  movingTiledImage.setRotation(movingTiledImage.getRotation(false) + deltaAngle, false);

  const newPosA1 = movingTiledImage.imageToViewportCoordinates(moveA.x, moveA.y, false);
  const newBounds = movingTiledImage.getBoundsNoRotate(false);
  const deltaPosition = posB1.minus(newPosA1);
  const newPosition = new OpenSeadragon.Point(newBounds.x, newBounds.y).plus(deltaPosition);
  movingTiledImage.setPosition(newPosition);
}

export function getRegistrationState(viewer, tiledImageMeta, fiducials) {
  const images = [];
  for (let i = 0; i < viewer.world.getItemCount(); i += 1) {
    const tiledImage = viewer.world.getItemAt(i);
    const meta = tiledImageMeta?.get(tiledImage);
    if (!meta?.itemId) continue;

    const bounds = tiledImage.getBoundsNoRotate(false);
    images.push({
      itemId: meta.itemId,
      name: meta.name,
      role: meta.role,
      position: { x: bounds.x, y: bounds.y },
      width: bounds.width,
      rotation: tiledImage.getRotation(false),
      flip: tiledImage.getFlip(),
      opacity: tiledImage.opacity,
    });
  }

  const payload = { version: COREG_VERSION, images };
  if (isCompleteFiducials(fiducials)) {
    payload.fiducials = fiducials;
  }
  return payload;
}

/**
 * 3×3 matrix mapping dermoscopic pixel coords → macroscopic pixel coords.
 * Derived from the current OpenSeadragon layer placement (includes nudges).
 */
export function computeDermToMacroMatrix(movingTiledImage, staticTiledImage) {
  if (!movingTiledImage || !staticTiledImage) return null;

  const contentSize = movingTiledImage.getContentSize();
  const width = contentSize?.x || 0;
  const height = contentSize?.y || 0;
  if (width <= 0 || height <= 0) return null;

  const toMacro = (dermX, dermY) => {
    const viewport = movingTiledImage.imageToViewportCoordinates(dermX, dermY, false);
    const macro = staticTiledImage.viewportToImageCoordinates(viewport);
    return { x: macro.x, y: macro.y };
  };

  const origin = toMacro(0, 0);
  const xAxis = toMacro(width, 0);
  const yAxis = toMacro(0, height);

  const a = (xAxis.x - origin.x) / width;
  const b = (yAxis.x - origin.x) / height;
  const tx = origin.x;
  const c = (xAxis.y - origin.y) / width;
  const d = (yAxis.y - origin.y) / height;
  const ty = origin.y;

  return {
    label: 'derm → macro (px)',
    rows: [
      [a, b, tx],
      [c, d, ty],
      [0, 0, 1],
    ],
    affine: { a, b, tx, c, d, ty },
  };
}

export function formatMatrixCell(value) {
  if (!Number.isFinite(value)) return '—';
  if (Math.abs(value) < 1e-12) return '0';
  return Number(value.toFixed(5)).toString();
}

export function formatFiducialPoint(point) {
  if (!point || !Number.isFinite(point.x) || !Number.isFinite(point.y)) return '—';
  return `${Math.round(point.x)}, ${Math.round(point.y)}`;
}

export function buildFiducialsPayload(movingItemId, staticItemId, points) {
  if (!movingItemId || !staticItemId || !points) return null;

  const payload = {
    movingItemId,
    staticItemId,
    points: {},
  };

  for (const key of ['moveA', 'moveB', 'ontoA', 'ontoB']) {
    const point = points[key];
    if (point && Number.isFinite(point.x) && Number.isFinite(point.y)) {
      payload.points[key] = { x: point.x, y: point.y };
    }
  }

  return Object.keys(payload.points).length > 0 ? payload : null;
}

export function isCompleteFiducials(fiducials) {
  const points = fiducials?.points;
  return Boolean(points?.moveA && points?.moveB && points?.ontoA && points?.ontoB);
}

function normalizeItemId(id) {
  if (id == null || id === '') return null;
  return String(id);
}

export function getCoregLayerFingerprint(layerState) {
  if (!layerState) return '';
  return JSON.stringify({
    itemId: normalizeItemId(layerState.itemId),
    x: layerState.position?.x,
    y: layerState.position?.y,
    width: layerState.width,
    rotation: layerState.rotation,
    flip: layerState.flip,
  });
}

export function getDermImageIds(dermItem) {
  if (!dermItem) return [];
  const ids = new Set();
  ['yamlId', '_id', 'itemid', 'isic_id', 'description'].forEach((key) => {
    const normalized = normalizeItemId(dermItem[key]);
    if (normalized) ids.add(normalized);
  });
  return [...ids];
}

/**
 * Find saved co-reg layer for a derm image.
 * @param {object|string} dermItemOrId — registration row or item id string
 * @param {{ strict?: boolean }} options — strict true (default): no fallback to unrelated moving layers
 */
export function findCoregMovingLayer(coregState, dermItemOrId, { strict = true } = {}) {
  if (!coregState?.images?.length) return null;

  const targetIds = new Set();
  if (typeof dermItemOrId === 'string' || typeof dermItemOrId === 'number') {
    const normalized = normalizeItemId(dermItemOrId);
    if (normalized) targetIds.add(normalized);
  } else if (dermItemOrId) {
    getDermImageIds(dermItemOrId).forEach((id) => targetIds.add(id));
  }

  if (targetIds.size === 0) return null;

  const movingLayers = coregState.images.filter((img) => img.role === 'moving');
  const match = movingLayers.find((img) => targetIds.has(normalizeItemId(img.itemId)));
  if (match) return match;

  if (strict) return null;
  return movingLayers.length === 1 ? movingLayers[0] : null;
}

export function transformPointWithMatrix(matrix, x, y) {
  if (matrix?.affine) {
    const { a, b, tx, c, d, ty } = matrix.affine;
    return { x: a * x + b * y + tx, y: c * x + d * y + ty };
  }
  if (matrix?.rows) {
    const [r0, r1] = matrix.rows;
    return {
      x: r0[0] * x + r0[1] * y + r0[2],
      y: r1[0] * x + r1[1] * y + r1[2],
    };
  }
  return { x: 0, y: 0 };
}

export function registrationFieldsFromMatrix(matrix, dermWidth, dermHeight) {
  const w = dermWidth || 0;
  const h = dermHeight || 0;
  const corners = [
    transformPointWithMatrix(matrix, 0, 0),
    transformPointWithMatrix(matrix, w, 0),
    transformPointWithMatrix(matrix, w, h),
    transformPointWithMatrix(matrix, 0, h),
  ];
  const xs = corners.map((p) => p.x);
  const ys = corners.map((p) => p.y);
  const coord = (p) => `[${Math.round(p.x)}, ${Math.round(p.y)}]`;

  return {
    a: coord(corners[0]),
    b: coord(corners[1]),
    c: coord(corners[2]),
    d: coord(corners[3]),
    x_min: Math.round(Math.min(...xs)),
    x_max: Math.round(Math.max(...xs)),
    y_min: Math.round(Math.min(...ys)),
    y_max: Math.round(Math.max(...ys)),
    derm_to_macro_matrix: matrix.rows || null,
  };
}

/** Build full coreg save payload including derm→macro matrix metadata. */
export function buildCoregSavePayload(
  viewer,
  tiledImageMeta,
  fiducials,
  movingTiledImage,
  staticTiledImage
) {
  const payload = getRegistrationState(viewer, tiledImageMeta, fiducials);
  const matrix = computeDermToMacroMatrix(movingTiledImage, staticTiledImage);
  if (matrix && movingTiledImage) {
    const dermSize = movingTiledImage.getContentSize();
    payload.dermToMacroMatrix = {
      rows: matrix.rows,
      affine: matrix.affine,
      dermWidth: dermSize?.x || 0,
      dermHeight: dermSize?.y || 0,
    };
  }
  return payload;
}

/** Merge co-registration results into the matching dermoscopic registrationData row. */
export function updateRegistrationDataForDerm(registrationData, dermItem, coregPayload) {
  if (!Array.isArray(registrationData) || !coregPayload?.dermToMacroMatrix) {
    return registrationData;
  }

  const dermId = dermItem?.yamlId || dermItem?._id;
  if (!dermId) return registrationData;

  const { dermToMacroMatrix } = coregPayload;
  const fields = registrationFieldsFromMatrix(
    dermToMacroMatrix,
    dermToMacroMatrix.dermWidth,
    dermToMacroMatrix.dermHeight
  );

  return registrationData.map((row) => {
    const rowId = row.yamlId || row._id;
    const type = (row.type || '').toLowerCase();
    const sameItem = rowId === dermId;
    const sameIsic = dermItem.isic_id && row.isic_id === dermItem.isic_id;
    if (!sameItem && !(type.includes('dermoscopic') && sameIsic)) {
      return row;
    }
    return { ...row, ...fields };
  });
}

export function applySavedRegistration(viewer, coregState, itemIdToTiledImage) {
  if (!coregState?.images?.length) return false;
  if (coregState.version !== undefined && coregState.version !== COREG_VERSION) {
    return false;
  }

  for (const saved of coregState.images) {
    const tiledImage = itemIdToTiledImage.get(saved.itemId);
    if (!tiledImage) continue;

    tiledImage.setPosition(new OpenSeadragon.Point(saved.position.x, saved.position.y), false);
    tiledImage.setWidth(saved.width, false);
    tiledImage.setRotation(saved.rotation, false);
    tiledImage.setFlip(saved.flip);
    tiledImage.setOpacity(saved.opacity);
  }

  const order = coregState.images
    .map((saved) => itemIdToTiledImage.get(saved.itemId))
    .filter(Boolean);
  order.forEach((tiledImage, index) => {
    viewer.world.setItemIndex(tiledImage, index);
  });

  return true;
}
